import React, {useContext, useEffect, useState} from 'react';
import {CaretDownOutlined, DeleteOutlined, InboxOutlined, PlusOutlined} from '@ant-design/icons';
import {Button, Divider, Form, Image, Modal, Row, Select, Spin, Upload} from 'antd';
import type {RcFile, UploadProps} from 'antd/es/upload';
import type {UploadFile} from 'antd/es/upload/interface';
import "../../assets/css/Dentobook.css";
import "../../assets/css/PatientPhotos.css";
import {FormattedMessage, useIntl} from "react-intl";
import {Patient} from "../../api-client";
import {AlertContext} from "../../context/AlertContext";
import {isMobileOnly} from "react-device-detect";
import {getPatientApi} from "../../api/apiInstances";
import logger from "../../utils/logger";
import {ALERT_DURATIONS} from "../../constants/alerts";

const {Dragger} = Upload;

interface PatientPhotosProps {
    patient: Patient;
}

const {Option} = Select;

const PatientPhotos = ({patient}: PatientPhotosProps) => {

    const PHOTO_URL = '/api/patients/photos/';

    const X_RAY_CATEGORY = 'X_RAY';
    const PATIENT_CATEGORY = 'PATIENT';

    const defaultValueArr = {
        [X_RAY_CATEGORY]: [],
        [PATIENT_CATEGORY]: []
    };
    const defaultValueBool = {
        [X_RAY_CATEGORY]: false,
        [PATIENT_CATEGORY]: false
    };

    const intl = useIntl();

    const {setVisible, setError} = useContext(AlertContext);

    const [isModalOpen, setIsModalOpen] = useState<boolean>(false);

    const [previewButtonsOpen, setPreviewButtonsOpen] = useState(false);

    const [isConfirmModalOpen, setConfirmModalOpen] = useState(false);

    const [categoryPreviewVisible, setCategoryPreviewVisible] = useState<{
        [index: string]: boolean
    }>({...defaultValueBool});

    const [previewOpen, setPreviewOpen] = useState(false);

    const previewImage: string = '';
    const previewTitle: string = '';

    const [photos, setPhotos] = useState<{ [index: string]: UploadFile[] }>({...defaultValueArr});

    const [categoryLoading, setCategoryLoading] = useState<{ [index: string]: boolean }>({...defaultValueBool});

    const [categoryError, setCategoryError] = useState<{ [index: string]: boolean }>({...defaultValueBool});

    const [initialLoading, setInitialLoading] = useState<boolean>(true);

    const [category, setCategory] = useState('X_RAY');

    const [uploadQueue, setUploadQueue] = useState<RcFile[]>([]);

    const [selectedAction, setSelectedAction] = useState<'DELETE' | 'MOVE' | ''>('');
    const [selectedPhoto, setSelectedPhoto] = useState<string>('');

    const patientApi = getPatientApi();

    const props: UploadProps = {
        showUploadList: false,
        name: 'file',
        beforeUpload: (file, fileList) => {
            const isValidFormat = file.type === 'image/jpeg' || file.type === 'image/jpg' || file.type === 'image/png';
            const isValidSize = file.size / 1024 / 1024 <= 10;  // Match backend limit of 10 MB

            if (!isValidFormat) {
                setError(intl.formatMessage({ id: 'invalid_photo_format' }));
                setVisible(true);
                setTimeout(() => setVisible(false), ALERT_DURATIONS.STANDARD);

                return Upload.LIST_IGNORE;
            }

            if (!isValidSize) {
                setError(intl.formatMessage({ id: 'invalid_photo_image_size' }));
                setVisible(true);
                setTimeout(() => setVisible(false), ALERT_DURATIONS.STANDARD);

                return Upload.LIST_IGNORE;
            }

            setUploadQueue(fileList);
            setIsModalOpen(true);

            return false;
        },
        multiple: true,
        onChange(info) {
            const { status } = info.file;
            if (status !== 'uploading') {
                logger.log(info.file, info.fileList);
            }
            if (status === 'done') {
                logger.log('Upload success');
            } else if (status === 'error') {
                logger.log('Upload error');
            }
        },
        onDrop(e) {
            logger.log('Dropped files', e.dataTransfer.files);
        },
    };


    useEffect(() => {
        loadPhotos();
    }, []);

    const loadPhotos = () => {
        if (!patient.id) {
            setInitialLoading(false);
            return;
        }

        setInitialLoading(true);
        patientApi.getPatientPhotos(patient.id, {})
            .then(res => {
                const photosObj = JSON.parse(JSON.stringify(defaultValueArr));

                res.data.forEach(item => {
                    photosObj[item.type!.toUpperCase()].push({
                        uid: item.id,
                        name: '',
                        status: 'done',
                        url: item.url,  // Use presigned R2 URL from backend
                    });
                });

                setPhotos(photosObj);
                setInitialLoading(false);
            })
            .catch(err => {
                logger.log(err);
                setInitialLoading(false);
            });
    }

    const handleCancel = () => setPreviewOpen(false);

    const handleOk = () => {
        setIsModalOpen(false);

        const categoryObject = {...categoryLoading};

        categoryObject[category] = true;

        setCategoryLoading(categoryObject);

        uploadFileQueue(uploadQueue);
    };

    let error = false;

    const uploadFileQueue = (rcFiles: RcFile[]) => {
        let completedUploads = 0;
        const totalUploads = rcFiles.length;

        for (const rcFile of rcFiles) {
            patientApi.uploadPatientPhoto(patient.id as string, rcFile as File, category as string, undefined, {})
                .then((res) => {
                    const photoObject = {...photos};

                    const photoId = res.data.id!;

                    photoObject[category].push({
                        uid: photoId,
                        name: '',
                        status: 'done',
                        url: res.data.url ?? undefined,  // Use presigned R2 URL from backend
                    })

                    setPhotos(photoObject);

                    completedUploads++;

                    // Only hide loading after all uploads complete
                    if (completedUploads === totalUploads) {
                        const categoryObject = {...categoryLoading};
                        categoryObject[category] = false;
                        setCategoryLoading(categoryObject);

                        if (!error) {
                            const categoryObject = {...categoryError};
                            for (const key of Object.keys(categoryObject)) {
                                categoryObject[key] = false;
                            }

                            setCategoryError(categoryObject);

                            setVisible(true);
                            setTimeout(() => setVisible(false), ALERT_DURATIONS.STANDARD);
                        }
                    }

                    error = false;
                })
                .catch(err => {
                    setVisible(true);

                    setError(err.response.data.message);
                    setTimeout(() => {
                        setVisible(false)
                        setError('');
                    }, ALERT_DURATIONS.STANDARD);

                    let categoryObject = {...categoryLoading};
                    for (const key of Object.keys(categoryObject)) {
                        categoryObject[key] = false;
                    }
                    setCategoryLoading(categoryObject);

                    categoryObject = {...categoryError};
                    categoryObject[category] = true;
                    setCategoryError(categoryObject);
                });
        }
    }

    const handleCancelModal = () => {
        setIsModalOpen(false);
        setUploadQueue([]);
    };

    const handleConfirmCancel = () => {
        setConfirmModalOpen(false);
    };

    const handleConfirmOk = () => {
        setConfirmModalOpen(false);

        if (selectedAction === 'MOVE') {
            patientApi.updatePhoto(selectedPhoto, 'PATIENT', undefined, {})
                .then(() => {
                    loadPhotos();

                    setVisible(true);
                    setTimeout(() => setVisible(false), ALERT_DURATIONS.STANDARD);
                })
                .catch(err => {
                    logger.log(err);
                });
        } else if (selectedAction === 'DELETE') {
            patientApi.deletePhoto(selectedPhoto)
                .then(() => {
                    loadPhotos();

                    setVisible(true);
                    setTimeout(() => setVisible(false), ALERT_DURATIONS.STANDARD);
                })
                .catch(err => {
                    logger.log(err);
                });
        }
    };

    const openConfirmModal = (action: 'MOVE' | 'DELETE') => {
        setConfirmModalOpen(true);
        setPreviewButtonsOpen(false);

        const categoryObject = {...categoryPreviewVisible};
        for (const key of Object.keys(categoryObject)) {
            categoryObject[key] = false;
        }

        setCategoryPreviewVisible(categoryObject);

        setSelectedAction(action);
    }

    return (
        <div className="dentobook-content" style={isMobileOnly ? {marginBottom: 150} : {}}>
            <Modal
                transitionName=""
                okText={<FormattedMessage id="confirm"/>}
                   style={{width: '100%', height: '100%'}}
                   className="dentobook-modal"
                   okButtonProps={{className: "modal-btn"}}
                   cancelButtonProps={{className: "modal-btn"}}
                   cancelText={<FormattedMessage id="cancel"/>}
                   open={isConfirmModalOpen} onOk={handleConfirmOk} onCancel={handleConfirmCancel}>
                <br/>
                <strong><FormattedMessage id="confirmation_text"/></strong>
            </Modal>
            {previewButtonsOpen ?
                <div className="upload-image-preview-wrapper">
                    <div className="upload-image-preview-buttons">
                        <Button shape="round"
                                style={{color: 'red', opacity: '0.8'}}
                                onClick={() => openConfirmModal('DELETE')}><DeleteOutlined/> <FormattedMessage
                            id={"delete_text"}/></Button>
                        <Button type="primary"
                                shape="round"
                                onClick={() => openConfirmModal('MOVE')}
                        ><FormattedMessage id={"move_text"}/></Button>
                    </div>
                </div>
                : null
            }
            <Modal
                transitionName=""
                className="dentobook-modal"
                title={<FormattedMessage id="choose_image_category"/>}
                okText={<FormattedMessage id="confirm"/>}
                okButtonProps={{className: "modal-btn"}}
                cancelButtonProps={{className: "modal-btn"}}
                cancelText={<FormattedMessage id="cancel"/>}
                width={600}
                open={isModalOpen} onOk={handleOk} onCancel={handleCancelModal}>
                <Form layout="vertical">
                    <Form.Item name="category"
                               className={"dentobook-form-item choose-photo-type-form-item"}
                               label={<FormattedMessage id="category"/>}>
                        <Select
                            onChange={e => setCategory(e)}
                            defaultValue="X_RAY"
                            suffixIcon={<CaretDownOutlined/>}
                        >
                            <Option value="X_RAY"><FormattedMessage id="x_ray_photos"/></Option>
                            <Option value="PATIENT"><FormattedMessage id="patient_photos"/></Option>
                        </Select>
                    </Form.Item>
                </Form>
            </Modal>
            {Object.keys(photos).map(currentCategory => (
                <React.Fragment key={currentCategory}>
                    <h4>{currentCategory === X_RAY_CATEGORY ?
                        <FormattedMessage id="x_ray_photos"/> :
                        <FormattedMessage id="patient_photos"/>}</h4>
                    {initialLoading ? (
                        <div style={{ textAlign: 'center', padding: '20px' }}>
                            <Spin size="small" />
                        </div>
                    ) : photos[currentCategory].length === 0 ? (
                        <FormattedMessage id="not_available"/>
                    ) : null}
                    <Upload
                        showUploadList={false}
                        listType="picture-card"
                        fileList={photos[currentCategory]}
                        className="photo-thumbnail"
                        // onChange={handleChange}
                    >
                    </Upload>
                    <Row>
                        <Image.PreviewGroup
                            preview={{
                                visible: categoryPreviewVisible[currentCategory],
                                onVisibleChange: (value, prevValue, currentIndex) => {
                                    const tmp = {...categoryPreviewVisible};
                                    tmp[currentCategory] = value;

                                    setCategoryPreviewVisible(tmp);

                                    setPreviewButtonsOpen(value);

                                    if (typeof currentIndex !== 'undefined') {
                                        setSelectedPhoto(photos[currentCategory][currentIndex].uid);
                                    }
                                }
                            }}
                        >
                            {photos[currentCategory].map(photo => (
                                <Image
                                    key={photo.uid}
                                    className="photo-img m-24"
                                    width={150}
                                    src={photo.url}/>
                            ))}
                        </Image.PreviewGroup>
                        {categoryLoading[currentCategory] ?
                            <div className="photo-img-loading m-24">
                                <Spin size="small" /> <span style={{marginLeft: '8px'}}>Uploading...</span>
                            </div> : null}
                    </Row>
                </React.Fragment>
            ))}
            <Modal
                transitionName=""
                className="dentobook-modal"
                open={previewOpen} title={previewTitle} footer={null}
                onCancel={handleCancel}>
                <img alt="example" style={{width: '100%'}} src={previewImage}/>
            </Modal>
            <Divider className="divider-none"/>
            <Dragger {...props}>
                <p className="dentobook-drag-icon">
                    <InboxOutlined/>
                </p>
                <p className="ant-upload-text dentobook-content"><FormattedMessage id="drag_or_choose"/></p>
                <p className="mt-3">
                    <Button className="dentobook-drag-btn" shape="round" type="default" size="large">
                        <PlusOutlined/> <FormattedMessage id="choose"/>
                    </Button>
                </p>
            </Dragger>
        </div>
    );
};

export default PatientPhotos;